// ═══════════════════════════════════════════════════════════
// i18n — Client-side initialization (i18next + react-i18next)
//
// Static imports ensure Vite bundles only the locales we ship.
// Language detection priority:
//   1. localStorage (user manually changed via switcher)
//   2. B24 LANG URL param (portal language from iframe POST)
//   3. Fallback: 'en'
// ═══════════════════════════════════════════════════════════
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// ─── English (source) ────────────────────────────────────
import enCommon from "./locales/en/common.json";
import enRules from "./locales/en/rules.json";
import enCommissions from "./locales/en/commissions.json";
import enSettings from "./locales/en/settings.json";
import enHelp from "./locales/en/help.json";
import enErrors from "./locales/en/errors.json";
import enNotifications from "./locales/en/notifications.json";

// ─── Spanish ─────────────────────────────────────────────
import esCommon from "./locales/es/common.json";
import esRules from "./locales/es/rules.json";
import esCommissions from "./locales/es/commissions.json";
import esSettings from "./locales/es/settings.json";
import esHelp from "./locales/es/help.json";
import esErrors from "./locales/es/errors.json";
import esNotifications from "./locales/es/notifications.json";

// ─── Portuguese ──────────────────────────────────────────
import ptCommon from "./locales/pt/common.json";
import ptRules from "./locales/pt/rules.json";
import ptCommissions from "./locales/pt/commissions.json";
import ptSettings from "./locales/pt/settings.json";
import ptHelp from "./locales/pt/help.json";
import ptErrors from "./locales/pt/errors.json";
import ptNotifications from "./locales/pt/notifications.json";

import type { SupportedLanguage } from "./types";

// ─── Constants ───────────────────────────────────────────

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ["en", "es", "pt"];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  es: "Español",
  pt: "Português",
};

export const I18N_NAMESPACES = [
  "common",
  "rules",
  "commissions",
  "settings",
  "help",
  "errors",
  "notifications",
] as const;

// ─── Language Detection ─────────────────────────────────

/** localStorage key written ONLY when user manually picks a language. */
export const I18N_STORAGE_KEY = "i18n_language";

function isSupportedLang(lang: string): lang is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
}

/**
 * Detect the initial language:
 *   1. localStorage — user manually changed via the LanguageSwitcher
 *   2. B24 `LANG` URL param — portal language from the iframe POST
 *   3. Fallback to 'en'
 */
function detectLanguage(): SupportedLanguage {
  // 1. Manual override persisted by the LanguageSwitcher
  try {
    const ls = (globalThis as any).localStorage;
    const stored = ls ? ls.getItem(I18N_STORAGE_KEY) : null;
    if (stored && isSupportedLang(stored)) return stored;
  } catch {
    // localStorage unavailable — continue
  }

  // 2. B24 LANG query parameter (e.g. ?LANG=en&DOMAIN=...)
  try {
    const loc = (globalThis as Record<string, unknown>)["location"] as
      | { search?: string }
      | undefined;
    const params = new URLSearchParams(loc?.search ?? "");
    const b24Lang = params.get("LANG")?.toLowerCase();
    if (b24Lang && isSupportedLang(b24Lang)) return b24Lang;
  } catch {
    // URL parsing failed — continue
  }

  // 3. Fallback
  return "en";
}

// ─── Init ────────────────────────────────────────────────

i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: enCommon,
      rules: enRules,
      commissions: enCommissions,
      settings: enSettings,
      help: enHelp,
      errors: enErrors,
      notifications: enNotifications,
    },
    es: {
      common: esCommon,
      rules: esRules,
      commissions: esCommissions,
      settings: esSettings,
      help: esHelp,
      errors: esErrors,
      notifications: esNotifications,
    },
    pt: {
      common: ptCommon,
      rules: ptRules,
      commissions: ptCommissions,
      settings: ptSettings,
      help: ptHelp,
      errors: ptErrors,
      notifications: ptNotifications,
    },
  },
  lng: detectLanguage(),
  fallbackLng: "en",
  defaultNS: "common",
  ns: [...I18N_NAMESPACES],
  interpolation: {
    escapeValue: false, // React already escapes
  },
});

export default i18n;
